import React, { useMemo, useContext } from 'react';
import classNames from 'classnames';
import FaqContext from '../../providers/Faq/Faq.context';
import { useTranslation } from 'react-i18next';
import { Question } from 'router/subrouters/DataRoom/models/QAndA';
import ShowShortenedRichText from 'router/subrouters/DataRoom/components/ShowShortenedRichText';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import usePastTime from 'hooks/usePastTime';
import useMatchMedia from 'hooks/useMatchMedia';
import TrashIcon from 'icons/Trash.icon';
import useManageQuestion from '../../hooks/useManageFaq';
import useDeleteQuestion from '../../hooks/useDeleteFaq';
import { FaqMobileCard } from '../FaqMobileCard';
import { Button } from 'ncoded-component-library';
import ChevrondownIcon from 'icons/Chevrondown.icon';
import useQueryParams from 'hooks/useQueryParams';
import ProductsIcon from 'icons/Products.icon';
import PencilIcon from 'icons/Pencil.icon';
import ArrowdownIcon from 'icons/Arrowdown.icon';
import { NewTag } from 'router/subrouters/DataRoom/components/NewTag';
import RelatedFileQa from 'router/subrouters/DataRoom/components/RelatedFileQa';
import { FIRST_PAGE } from 'data/global';
import { getQuestionSubject } from 'router/subrouters/DataRoom/utils';

import './FaqListTable.styles.scss';
import './FaqListTable.styles.responsive.scss';

type FaqListTableProps = {
  className?: string;
};

const TABLE_COLUMNS = [
  'question',
  'subject',
  'published',
  'delete',
  'manage',
] as const;

const COPY_HEADING_CELL = {
  question: 'questionOnly',
  subject: 'subject',
  published: 'published',
  delete: '',
  manage: '',
};

const SORT_COL_KEYS = {
  question: '',
  subject: 'sortSubject',
  manage: '',
  delete: '',
  published: 'publishedDateToFAQ',
};

const BODY_CONTENT_GENERATE: Record<
  typeof TABLE_COLUMNS[number],
  (
    val: Question,
    updateQuestion?: (question: Partial<Question>) => void,
  ) => React.ReactNode
> = {
  subject: ({ subject, otherSubject }: Question) => (
    <div className="dr-subject-cell">
      <p>{getQuestionSubject(subject, otherSubject)}</p>
    </div>
  ),
  question: (question, updateQuestion) => (
    <SubjectCellComponent question={question} updateQuestion={updateQuestion} />
  ),
  published: (question: Question) => (
    <PublishedCellComponent question={question} />
  ),
  delete: (question: Question) => <DeleteCellComponent question={question} />,
  manage: (question: Question) => <ManageCellComponent question={question} />,
};

const FaqListTable: React.FC<FaqListTableProps> = (props) => {
  const { className } = props;
  const { t } = useTranslation();
  const { displayedQuestions, canUserAddFAQ, updateQuestion } =
    useContext(FaqContext);
  const {
    setQueryParam,
    params: { sort },
  } = useQueryParams<{ sort: string; page: string }>();

  const classes = classNames('faq-list-table', className);

  const isTablet = useMatchMedia('isTablet');

  const tableColumns = useMemo(
    () =>
      TABLE_COLUMNS.filter(
        (c) => canUserAddFAQ || (c !== 'manage' && c !== 'delete'),
      ),
    [canUserAddFAQ],
  );

  const TableHeading = useMemo(
    () => (
      <thead>
        <tr>
          {tableColumns.map((col) => {
            const content = t(COPY_HEADING_CELL[col]);
            const sortingVal = SORT_COL_KEYS[col];

            const isSelected = sortingVal && sort?.includes(sortingVal);

            return (
              <th key={col} className={col}>
                <div
                  className={classNames({
                    selected: isSelected,
                    rotated: sort !== sortingVal,
                  })}
                >
                  {content}
                  {sortingVal && (
                    <Button
                      onClick={() => {
                        if (sort === sortingVal) {
                          setQueryParam('sort', `-${sortingVal}`);
                        } else {
                          setQueryParam('sort', sortingVal);
                        }
                        setQueryParam('page', FIRST_PAGE);
                      }}
                      className={classNames({
                        selected: isSelected,
                        rotated: sort === sortingVal,
                      })}
                    >
                      {isSelected ? (
                        <ArrowdownIcon />
                      ) : (
                        <>
                          <ChevrondownIcon /> <ChevrondownIcon />
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </th>
            );
          })}
        </tr>
      </thead>
    ),
    [tableColumns, t, sort, setQueryParam],
  );

  if (!displayedQuestions.length) return null;

  if (isTablet)
    return (
      <div className={classes}>
        {displayedQuestions.map((el) => (
          <FaqMobileCard
            key={el._id}
            question={el}
            hasManagePermission={canUserAddFAQ}
          />
        ))}
      </div>
    );

  return (
    <table className={classes}>
      {TableHeading}
      <tbody>
        {displayedQuestions.map((q) => (
          <tr key={q._id}>
            {tableColumns.map((column) => {
              return (
                <td key={column} className={column}>
                  {BODY_CONTENT_GENERATE[column](q, updateQuestion)}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default FaqListTable;

type CellProps = {
  className?: string;
  question: Question;
  updateQuestion?: (question: Partial<Question>) => void;
};

export const SubjectCellComponent: React.FC<CellProps> = ({
  className,
  question,
  updateQuestion,
}) => {
  const { subject, otherSubject, content, publishedDateToFAQ, dataRoomAnswer } =
    question;

  const { isBuySide } = useContext(DataRoomContext);

  const isTablet = useMatchMedia('isTablet');

  return (
    <div className={`subject-cell ${className}`}>
      <div className="subject-cell__question-wrapper">
        <ShowShortenedRichText
          question={question}
          hidePrivateInfo={isBuySide}
          className="subject-cell__question-shortened"
          maxHeight={120}
          key={content}
          asAccordion
          updateQuestion={updateQuestion}
        >
          {content}
        </ShowShortenedRichText>

        <NewTag time={publishedDateToFAQ} />
      </div>

      {dataRoomAnswer && (
        <div className="subject-cell__answer-shortened">
          <ShowShortenedRichText
            question={question}
            hidePrivateInfo={isBuySide}
            key={dataRoomAnswer.content}
            maxHeight={120}
            asAccordion
            updateQuestion={updateQuestion}
          >
            {dataRoomAnswer.content}
          </ShowShortenedRichText>
        </div>
      )}

      <div className="tags">
        {isTablet && (
          <p>
            <ProductsIcon />
            {getQuestionSubject(subject, otherSubject)}
          </p>
        )}

        <RelatedFileQa question={question} />
      </div>
    </div>
  );
};

export const PublishedCellComponent: React.FC<CellProps> = ({ question }) => {
  const { publishedDateToFAQ } = question;

  const pastTime = usePastTime()(publishedDateToFAQ);

  if (!pastTime) return null;

  return <div className="dr-published-cell">{pastTime}</div>;
};

const DeleteCellComponent: React.FC<CellProps> = ({ question }) => {
  const { canDeleteFaq, onDelete } = useDeleteQuestion(question);
  const { doesntHaveAccess } = useContext(DataRoomContext);

  if (doesntHaveAccess || !canDeleteFaq) return null;

  return (
    <Button variant="link" className="warning-action-button" onClick={onDelete}>
      <TrashIcon />
    </Button>
  );
};

const ManageCellComponent: React.FC<CellProps> = ({ question }) => {
  const { isPublishedToFAQ } = question;
  const { t } = useTranslation();
  const { ManageDrawer, openDrawer } = useManageQuestion(question);
  const { doesntHaveAccess } = useContext(DataRoomContext);

  if (doesntHaveAccess) return null;

  return (
    <>
      {isPublishedToFAQ ? (
        <Button variant="outline" onClick={openDrawer}>
          <PencilIcon />
          {t('edit')}
        </Button>
      ) : (
        <Button variant="link" onClick={openDrawer}>
          {t('manage')}
        </Button>
      )}

      {ManageDrawer}
    </>
  );
};
